import React, { useContext, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { get } from 'lodash';
import config from 'Config';
import logger from 'Logger/logger';
import { t } from 'Src/i18n';
import { Title } from 'Components/frameworks/title/Title';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { WeChatTimer } from 'Components/widgets/payment-info/payment-process/WechatTimer';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { asyncPolling } from 'Utils/polling/polling';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import { PAYMENT_STATUS_MUTATION } from 'Utils/request/requestDictionary';
import { API_PAYMENT_STATUS_PENDING } from 'Utils/payment/constants';
import { handlePspResponse } from 'Components/widgets/payment-info/payment-process/WeChatQRCode.utils';
import { useAppContext } from 'Context/ReactAppContext';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { PaymentInfoContext } from 'Components/widgets/payment-info/PaymentInfoContext';
const QRCode = dynamic(() => import('qrcode.react'), {
  ssr: false,
});
const POLLING_CONFIG = config.get('psp.ewechat.polling');
type Props = {
  currency: string;
  qrCodeUrl: string;
  amountToPay: number;
  orderId: string;
  customerId: string;
  timeoutCallback: () => void;
};
export const WeChatQRCode = ({
  currency,
  qrCodeUrl,
  amountToPay,
  orderId,
  customerId,
  timeoutCallback,
}: Props) => {
  const appContext = useAppContext();
  const { query: queryParams } = useRouter();
  const timeoutInSeconds = POLLING_CONFIG.timeout / 1000;
  const { bookingStatus } = useContext(PaymentInfoContext);
  useEffect(() => {
    const paymentStatusMutation = `
      mutation {
        paymentStatus(paymentId : "${orderId}") {
          finalPaymentResponse {
            payment {
              paymentStatus
              providerId
              paymentAmount
              currency
            }
            booking {
              bookingId
            }
          }
          errors
        }
      }
    `;
    asyncPolling({
      delay: POLLING_CONFIG.frequency,
      // 10 seconds
      timeout: POLLING_CONFIG.timeout,
      // 5 minutes
      pollingFunction: () =>
        graphqlClient.runNoCacheQuery(paymentStatusMutation, appContext, {
          requestName: PAYMENT_STATUS_MUTATION,
        }),
      shouldPollAgain: (response) => {
        const paymentStatus = get(
          response,
          'paymentStatus.finalPaymentResponse.payment.paymentStatus',
        );
        return paymentStatus === API_PAYMENT_STATUS_PENDING;
      },
      onResponse: (response) =>
        handlePspResponse({
          response,
          customerId,
          queryParams,
          orderId,
          bookingStatus,
        }),
    }).catch((e) => {
      logger.error(e);
      timeoutCallback();
    });
  }, [orderId, customerId]);
  return (
    <div className="w-full">
      <Title className="mb-10" theme="title-brand-n2">
        {t('common.payment.wechat.qrCode.title')}
      </Title>
      <Capsule header="none">
        <div className="flex justify-center pt-[33px]">
          {t('common.payment.wechat.qrCode.pay')}:
          <span className="text-orange">
            {formatAmountForLocale(Math.abs(amountToPay), appContext.locale, currency)}
          </span>
        </div>

        <WeChatTimer timeout={timeoutInSeconds} />

        <QRCode value={qrCodeUrl} size={239} className="m-auto block p-30" />

        <div className="pb-60 text-center">
          <Icon className="align-middle" name={ICONS.WECHATPAYMENT} width="1.5rem" />
          <div className="ms-10 inline-block">{t('common.payment.wechat.qrCode.scan')}</div>
        </div>
      </Capsule>
    </div>
  );
};
